import React from 'react';
import PropTypes from 'prop-types';

import { withErrorBoundary } from 'react-use-error-boundary';

import useSiteMetadata from '~/hooks/useSiteMetadata';
import { includes } from 'lodash';

const Seo = withErrorBoundary(
    ({
        title = '',
        description = '',
        image = '',
        url = '',
        location = { pathname: '' },
        price = 0.0,
        sku = '',
        seoFragment = {},
        children = null
    }) => {
        const {
            title: defaultTitle,
            description: defaultDescription,
            image: defaultImage,
            siteUrl
        } = useSiteMetadata();

        const defaultUrl = siteUrl.replace(/\/?$/, '/'); // Add trailing slash.
        const pathname = location.pathname.replace(/^\/+/, ''); // Remove leading slash.

        const seo = {
            title: title || seoFragment?.title || defaultTitle,
            description:
                description || seoFragment?.metaDesc || defaultDescription,
            image: image || defaultImage,
            url: url || `${defaultUrl}${pathname}`
        };

        const twitter = {
            card: 'summary_large_image',
            title: seoFragment?.twitterTitle || seo.title,
            description: seoFragment?.twitterDescription || seo.description,
            image:
                seoFragment?.twitterImage?.sourceUrl ||
                seoFragment?.opengraphImage?.sourceUrl ||
                seo.image,
            url: seo.url
        };

        const opengraph = {
            type: seoFragment?.opengraphType || 'page',
            title: seoFragment?.opengraphTitle || seo.title,
            description: seoFragment?.opengraphDescription || seo.description,
            image: seoFragment?.opengraphImage?.sourceUrl || seo.image,
            url: seo.url
        };

        let schema = seoFragment.schema?.raw;
        const isProduct =
            seoFragment.schema?.pageType &&
            includes(seoFragment.schema?.pageType, 'ItemPage');

        if (isProduct) {
            schema = `
                {
                    "@context": "https://schema.org/",
                    "@type": "Product",
                    "name": "${seo.title}",
                    "image": [
                        "${twitter.image}"
                    ],
                    "description": "${seo.description}",
                    "sku": "${sku}",
                    "brand": {
                        "@type": "Thing",
                        "name": "Autobell Car Wash"
                    },
                    "offers": {
                        "@type": "Offer",
                        "url": "${seo.url}",
                        "priceCurrency": "USD",
                        "price": "${price}"
                    }
                }
            `;
        }

        return (
            <>
                <html lang="en-us" />

                <title>{seo.title}</title>

                <meta
                    name="robots"
                    content={`${seoFragment?.metaRobotsNoindex}, ${seoFragment?.metaRobotsNofollow}`}
                />

                <meta name="description" content={seo.description} />
                <meta name="image" content={seo.image} />

                <meta name="twitter:card" content={twitter.card} />
                <meta name="twitter:title" content={twitter.title} />
                <meta name="twitter:url" content={twitter.url} />
                <meta
                    name="twitter:description"
                    content={twitter.description}
                />
                <meta name="twitter:image" content={twitter.image} />

                <meta property="og:title" content={opengraph.title} />
                <meta property="og:type" content={opengraph.type} />
                <meta property="og:url" content={opengraph.url} />
                <meta
                    property="og:description"
                    content={opengraph.description}
                />
                <meta property="og:image" content={opengraph.image} />

                {schema ? (
                    <script type="application/ld+json">{schema}</script>
                ) : null}

                {children}
            </>
        );
    }
);

Seo.propTypes = {
    title: PropTypes.string,
    description: PropTypes.string,
    image: PropTypes.string,
    url: PropTypes.string,
    pathname: PropTypes.string,
    location: PropTypes.shape({
        pathname: PropTypes.string
    }),
    price: PropTypes.number,
    sku: PropTypes.string,
    seoFragment: PropTypes.shape({
        metaDesc: PropTypes.string,
        title: PropTypes.string,
        twitterTitle: PropTypes.string,
        twitterDescription: PropTypes.string,
        twitterImage: PropTypes.shape({
            sourceUrl: PropTypes.string
        }),
        opengraphTitle: PropTypes.string,
        opengraphDescription: PropTypes.string,
        opengraphType: PropTypes.string,
        opengraphImage: PropTypes.shape({
            sourceUrl: PropTypes.string
        })
    }),
    children: PropTypes.shape({})
};

export default Seo;
